import React, { useState, useEffect } from 'react';
import { walletAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const card = { background: '#fff', border: '0.5px solid rgba(0,0,0,0.07)', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };
const input = { width: '100%', padding: '8px 11px', background: '#F0EDE5', border: '0.5px solid rgba(0,0,0,0.1)', borderRadius: '7px', fontSize: '13px', color: '#1A1916', outline: 'none', fontFamily: 'DM Sans, sans-serif', transition: 'border 0.15s' };

export default function Wallet() {
  const { user } = useAuth();
  const [wallet, setWallet]         = useState(null);
  const [transactions, setTx]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showModal, setShowModal]   = useState(false);
  const [payMode, setPayMode]       = useState('virement');
  const [montant, setMontant]       = useState('');
  const [processing, setProcessing] = useState(false);
  const [toast, setToast]           = useState(null);

  // ─── Référence virement dynamique : NOMCABINET-JJMMAAAA ─────────────────
  // Ex : "ISELL CONSEILS" + 25/05/2026 → "ISELLCONSEILS-25052026"
  // Caractères non alphanum supprimés, espaces enlevés, tronqué à 20 chars max
  const referenceVirement = (() => {
    const rawName = user?.workspace?.name || user?.prenom || 'WALLET';
    const slug = rawName
      .toUpperCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')  // enlever accents
      .replace(/[^A-Z0-9]/g, '')                          // garder lettres+chiffres
      .slice(0, 20);
    const d = new Date();
    const date = `${String(d.getDate()).padStart(2, '0')}${String(d.getMonth() + 1).padStart(2, '0')}${d.getFullYear()}`;
    return `${slug}-${date}`;
  })();

  // Config
  const [seuil, setSeuil]               = useState('3000');
  const [rechargeAuto, setRechargeAuto] = useState('3000');
  const [autoPause, setAutoPause]       = useState(true);
  const [autoRecharge, setAutoRecharge] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [wr, tr] = await Promise.all([walletAPI.get(), walletAPI.transactions({ limit: 20 })]);
      setWallet(wr.data);
      setTx(tr.data || []);
      if (wr.data?.alert_threshold) setSeuil(String(wr.data.alert_threshold));
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const showToast = (msg, type = 'info') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleRecharge = async () => {
    if (!montant || Number(montant) < 100) { showToast('Montant minimum 100 €', 'error'); return; }
    setProcessing(true);
    try {
      if (payMode === 'virement') {
        await walletAPI.recharge({ montant: Number(montant), reference_virement: referenceVirement });
        showToast('Instructions de virement envoyées par email ✓', 'success');
        setShowModal(false); setMontant('');
      } else {
        showToast('Paiement CB disponible prochainement. Utilisez le virement pour l\'instant.', 'info');
      }
    } catch (err) { showToast(err.response?.data?.detail || 'Erreur', 'error'); }
    finally { setProcessing(false); }
  };

  const saveConfig = async () => {
    setSavingConfig(true);
    try {
      await walletAPI.updateAlert({ alert_threshold: Number(seuil), auto_pause: autoPause });
      showToast('Configuration sauvegardée ✓', 'success');
    } catch (err) { showToast('Erreur lors de la sauvegarde', 'error'); }
    finally { setSavingConfig(false); }
  };

  if (loading) return <div style={{ padding: '3rem', textAlign: 'center', color: 'rgba(26,25,22,0.4)', fontSize: '13px' }}>Chargement…</div>;

  const balance   = wallet ? Number(wallet.balance) : 0;
  const threshold = wallet ? Number(wallet.alert_threshold) : 3000;
  const walletOk  = balance > threshold;
  const walletPct = Math.min(100, Math.round((balance / (threshold * 3.8)) * 100));

  const TYPE_CONFIG = {
    credit:        { label: 'Crédit',        color: '#1A7A4A', bg: 'rgba(26,122,74,0.08)',  icon: '+' },
    debit_inpi:    { label: 'Avance INPI',    color: '#B03020', bg: 'rgba(176,48,32,0.08)', icon: '−' },
    debit_greffe:  { label: 'Avance greffe',  color: '#B03020', bg: 'rgba(176,48,32,0.08)', icon: '−' },
    debit_jal:     { label: 'Frais JAL',      color: '#B03020', bg: 'rgba(176,48,32,0.08)', icon: '−' },
    remboursement: { label: 'Remboursement',  color: '#1A55A0', bg: 'rgba(26,85,160,0.08)', icon: '↩' },
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' }}>
        <div>
          <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '18px', fontWeight: 600, color: '#1A1916' }}>Wallet INPI</div>
          <div style={{ fontSize: '12px', color: 'rgba(26,25,22,0.4)', marginTop: '2px' }}>Avances pour frais de greffe et INPI</div>
        </div>
        <button onClick={() => setShowModal(true)} style={{ background: '#B8860B', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: '7px', fontSize: '12px', fontWeight: 500, cursor: 'pointer' }}>
          Recharger →
        </button>
      </div>

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginBottom: '1.25rem' }}>
        <div style={{ ...card, padding: '14px 16px' }}>
          <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'rgba(26,25,22,0.28)', marginBottom: '4px' }}>Solde actuel</div>
          <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '28px', fontWeight: 700, color: walletOk ? '#1A7A4A' : '#d97706' }}>
            {balance.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
          </div>
          <div style={{ marginTop: '8px' }}>
            <div style={{ height: '4px', background: '#F0EDE5', borderRadius: '2px', overflow: 'hidden', marginBottom: '4px' }}>
              <div style={{ width: `${walletPct}%`, height: '100%', background: walletOk ? '#1A7A4A' : '#d97706', borderRadius: '2px', transition: 'width 0.4s' }} />
            </div>
            <div style={{ fontSize: '10px', color: 'rgba(26,25,22,0.35)' }}>~{Math.floor(balance / 40)} dossiers · seuil {threshold.toLocaleString('fr-FR')} €</div>
          </div>
        </div>
        <div style={{ ...card, padding: '14px 16px' }}>
          <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'rgba(26,25,22,0.28)', marginBottom: '4px' }}>Consommé ce mois</div>
          <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '28px', fontWeight: 700, color: '#1A1916' }}>
            {transactions.filter(t => t.type !== 'credit' && t.type !== 'remboursement').reduce((s, t) => s + Math.abs(Number(t.montant)), 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })} €
          </div>
        </div>
        <div style={{ ...card, padding: '14px 16px', ...(walletOk ? {} : { background: 'rgba(217,119,6,0.04)', border: '0.5px solid rgba(217,119,6,0.2)' }) }}>
          <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'rgba(26,25,22,0.28)', marginBottom: '4px' }}>Seuil d'alerte</div>
          <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '28px', fontWeight: 700, color: walletOk ? '#1A1916' : '#d97706' }}>
            {threshold.toLocaleString('fr-FR')} €
          </div>
          <div style={{ fontSize: '11px', marginTop: '5px', color: autoPause ? '#B03020' : 'rgba(26,25,22,0.4)' }}>
            {autoPause ? 'Auto-pause activée' : 'Auto-pause désactivée'}
          </div>
        </div>
      </div>

      {/* Transactions + Config */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '1rem' }}>

        {/* Transactions */}
        <div style={card}>
          <div style={{ padding: '12px 16px', borderBottom: '0.5px solid rgba(0,0,0,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '13px', fontWeight: 600, color: '#1A1916' }}>Historique</div>
            <button style={{ background: 'transparent', border: '0.5px solid rgba(0,0,0,0.1)', borderRadius: '5px', padding: '3px 10px', fontSize: '11px', cursor: 'pointer', color: 'rgba(26,25,22,0.5)' }}>
              Exporter CSV
            </button>
          </div>
          {transactions.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'rgba(26,25,22,0.35)', fontSize: '12px' }}>Aucune transaction.</div>
          ) : transactions.map((t, i) => {
            const tc = TYPE_CONFIG[t.type] || { label: t.type, color: '#888', bg: '#f0f0f0', icon: '•' };
            const isCredit = t.type === 'credit' || t.type === 'remboursement';
            return (
              <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '11px 16px', borderBottom: i < transactions.length - 1 ? '0.5px solid rgba(0,0,0,0.05)' : 'none' }}>
                <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: tc.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: 700, color: tc.color, flexShrink: 0 }}>
                  {tc.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: '12px', fontWeight: 500, color: '#1A1916' }}>{t.description || tc.label}</div>
                  <div style={{ fontSize: '11px', color: 'rgba(26,25,22,0.4)', marginTop: '1px' }}>
                    {t.reference && `${t.reference} · `}{new Date(t.created_at).toLocaleDateString('fr-FR')}
                  </div>
                </div>
                <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '13px', fontWeight: 600, color: isCredit ? '#1A7A4A' : '#B03020' }}>
                  {isCredit ? '+' : '−'}{Math.abs(Number(t.montant)).toLocaleString('fr-FR')} €
                </div>
              </div>
            );
          })}
        </div>

        {/* Configuration */}
        <div style={{ ...card, padding: '1.1rem 1.3rem', alignSelf: 'start' }}>
          <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '13px', fontWeight: 600, color: '#1A1916', marginBottom: '1rem' }}>Configuration</div>

          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'rgba(26,25,22,0.35)', marginBottom: '5px' }}>Seuil d'alerte (€)</div>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <input style={{ ...input, width: '110px' }} type="number" value={seuil} onChange={e => setSeuil(e.target.value)} min="100" />
              <span style={{ fontSize: '11px', color: 'rgba(26,25,22,0.4)' }}>→ notif email</span>
            </div>
          </div>

          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'rgba(26,25,22,0.35)', marginBottom: '5px' }}>Montant de recharge auto (€)</div>
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <input style={{ ...input, width: '110px' }} type="number" value={rechargeAuto} onChange={e => setRechargeAuto(e.target.value)} min="100" />
              <span style={{ fontSize: '11px', color: 'rgba(26,25,22,0.4)' }}>→ si solde &lt; seuil</span>
            </div>
          </div>

          {[
            { label: 'Auto-pause dossiers',    desc: 'Stoppe les dépôts si wallet vide',   val: autoPause,    set: setAutoPause },
            { label: 'Recharge automatique',   desc: 'Via carte enregistrée',               val: autoRecharge, set: setAutoRecharge },
          ].map(opt => (
            <div key={opt.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '0.5px solid rgba(0,0,0,0.06)' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#1A1916' }}>{opt.label}</div>
                <div style={{ fontSize: '10px', color: 'rgba(26,25,22,0.38)', marginTop: '1px' }}>{opt.desc}</div>
              </div>
              <input type="checkbox" checked={opt.val} onChange={e => opt.set(e.target.checked)}
                style={{ width: '15px', height: '15px', accentColor: '#B8860B', cursor: 'pointer' }} />
            </div>
          ))}

          <button onClick={saveConfig} disabled={savingConfig} style={{ width: '100%', marginTop: '12px', background: '#B8860B', color: '#fff', border: 'none', padding: '8px', borderRadius: '7px', fontSize: '12px', fontWeight: 500, cursor: savingConfig ? 'not-allowed' : 'pointer', opacity: savingConfig ? 0.7 : 1 }}>
            {savingConfig ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
      </div>

      {/* Modal recharge */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}
          onClick={e => e.target === e.currentTarget && setShowModal(false)}>
          <div style={{ ...card, padding: '2rem', width: '100%', maxWidth: '480px' }}>
            <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '16px', fontWeight: 600, color: '#1A1916', marginBottom: '1.25rem' }}>Recharger le wallet INPI</div>

            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'rgba(26,25,22,0.4)', marginBottom: '8px' }}>Montant (€)</div>
              <div style={{ display: 'flex', gap: '6px', marginBottom: '8px' }}>
                {[1000, 5000, 10000, 20000].map(m => (
                  <button key={m} onClick={() => setMontant(String(m))} style={{
                    flex: 1, padding: '8px 4px', border: `1px solid ${montant === String(m) ? '#B8860B' : 'rgba(0,0,0,0.1)'}`,
                    background: montant === String(m) ? 'rgba(184,134,11,0.08)' : '#F0EDE5',
                    borderRadius: '7px', cursor: 'pointer', fontSize: '11px', fontWeight: montant === String(m) ? 500 : 300,
                    color: montant === String(m) ? '#B8860B' : 'rgba(26,25,22,0.6)', fontFamily: 'DM Sans, sans-serif',
                  }}>{m.toLocaleString('fr-FR')} €</button>
                ))}
              </div>
              <input style={input} type="number" value={montant} onChange={e => setMontant(e.target.value)} placeholder="Ou saisir un montant…" min={100} />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <div style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.07em', color: 'rgba(26,25,22,0.4)', marginBottom: '8px' }}>Mode de paiement</div>
              <div style={{ display: 'flex', gap: '8px' }}>
                {[{ key: 'virement', label: '🏦 Virement bancaire' }, { key: 'cb', label: '💳 Carte bancaire' }].map(m => (
                  <button key={m.key} onClick={() => setPayMode(m.key)} style={{
                    flex: 1, padding: '10px', border: `1px solid ${payMode === m.key ? '#1A1916' : 'rgba(0,0,0,0.1)'}`,
                    background: payMode === m.key ? '#1A1916' : 'transparent',
                    color: payMode === m.key ? '#fff' : 'rgba(26,25,22,0.6)',
                    borderRadius: '7px', cursor: 'pointer', fontSize: '12px', fontFamily: 'DM Sans, sans-serif',
                  }}>{m.label}</button>
                ))}
              </div>
            </div>

            {payMode === 'virement' && (
              <div style={{ background: '#F0EDE5', border: '0.5px solid rgba(0,0,0,0.07)', borderRadius: '8px', padding: '12px 14px', marginBottom: '1rem' }}>
                {[
                  ['Bénéficiaire', 'SWAP BUSINESS SAS'],
                  ['IBAN', 'FR76 1751 5900 0008 0153 2026 594'],
                  ['BIC', 'CEPAFRPP751'],
                  ['Référence', referenceVirement],
                ].map(([l, v]) => (
                  <div key={l} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '0.5px solid rgba(0,0,0,0.06)', fontSize: '12px' }}>
                    <span style={{ color: 'rgba(26,25,22,0.45)' }}>{l}</span>
                    <span style={{ fontWeight: 500, color: '#1A1916', fontFamily: l === 'Référence' ? 'monospace' : 'inherit' }}>{v}</span>
                  </div>
                ))}
              </div>
            )}

            {payMode === 'cb' && (
              <div style={{ background: 'rgba(26,85,160,0.06)', border: '0.5px solid rgba(26,85,160,0.15)', borderRadius: '8px', padding: '12px', marginBottom: '1rem', fontSize: '12px', color: '#1A55A0' }}>
                💳 Paiement CB disponible prochainement. Utilisez le virement pour l'instant.
              </div>
            )}

            <div style={{ background: 'rgba(217,119,6,0.06)', border: '0.5px solid rgba(217,119,6,0.15)', borderRadius: '7px', padding: '10px 12px', marginBottom: '1rem', fontSize: '11px', color: '#d97706' }}>
              ⚠ Votre wallet sera crédité sous 24h après réception du virement.
            </div>

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowModal(false)} style={{ background: 'transparent', border: '0.5px solid rgba(0,0,0,0.12)', borderRadius: '7px', padding: '8px 16px', fontSize: '12px', cursor: 'pointer', color: 'rgba(26,25,22,0.6)' }}>
                Annuler
              </button>
              <button onClick={handleRecharge} disabled={processing || !montant} style={{ background: '#B8860B', color: '#fff', border: 'none', padding: '8px 18px', borderRadius: '7px', fontSize: '12px', fontWeight: 500, cursor: processing ? 'not-allowed' : 'pointer', opacity: !montant || processing ? 0.6 : 1 }}>
                {processing ? 'Traitement…' : payMode === 'cb' ? 'Payer →' : 'Recevoir les instructions →'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div style={{ position: 'fixed', bottom: '1.5rem', right: '1.5rem', padding: '10px 16px', borderRadius: '8px', fontSize: '13px', zIndex: 300, background: toast.type === 'success' ? '#1A7A4A' : toast.type === 'error' ? '#B03020' : '#1A1916', color: '#fff', fontFamily: 'DM Sans, sans-serif' }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}
